import React from 'react';
import { Switch, FormControlLabel } from '@mui/material';
import OfficeBridgeDayService from '../../../Services/OfficeBridgeDayService';
import PublicHolidayService from '../../../Services/PublicHolidayService';
import OfficeService from '../../../Services/OfficeService';
import AuthService from '../../../Services/authService';
import ErrorMessage from '../../../Elements/ErrorMessage';
import Loading from '../../../Elements/loading';
import FaIcon from '../../../Elements/FaIcon';

// Status codes mirror AvailabilityPlannerConstants.BridgeDayStatus on the micro.
const STATUS = {
  NotSet: { label: 'Not set', className: 'bg-warning text-dark' },
  Configured: { label: 'Configured', className: 'bg-success' },
  SkippedIntentionally: { label: 'No bridge days this year', className: 'bg-secondary' },
};

// Office Calendar: per office and year, HR/Admin manage the BRIDGE DAYS (moved-day pairs) and the PUBLIC
// HOLIDAYS. Holiday hand-edits always win over the future API import (the backend tombstones deleted
// imported rows so a reload can't resurrect them).
export default class OfficeCalendarAdmin extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      offices: [],
      officeId: 0,
      year: new Date().getFullYear(),
      data: null,
      holidays: [],
      loading: true,
      newFrom: '',
      newTo: '',
      newHolidayDate: '',
      newHolidayName: '',
    };
    // The whole page is restricted to HR/Admin (menu + this guard + the micro's operations).
    // NOTE: confirm the exact HR role claim string for the Azure AD app roles (see the micro's EditRoles).
    this.canEdit = AuthService.HasRole('HR');
  }

  componentDidMount() {
    if (!this.canEdit) {
      this.setState({ loading: false });
      return;
    }
    OfficeService.GetOfficesForSelect()
      .then((offices) => {
        const list = Array.isArray(offices) ? offices : [];
        const officeId = list.length > 0 ? list[0].officeId : 0;
        this.setState({ offices: list, officeId }, () => this.load());
      })
      .catch(() => {
        ErrorMessage.SetErrorMessage('BD28');
        this.setState({ loading: false });
      });
  }

  load() {
    if (!this.state.officeId) {
      this.setState({ loading: false });
      return;
    }
    this.setState({ loading: true });
    Promise.all([
      OfficeBridgeDayService.Get(this.state.officeId, this.state.year),
      PublicHolidayService.Get(this.state.officeId, this.state.year),
    ])
      .then(([data, holidays]) =>
        this.setState({ data: data || null, holidays: Array.isArray(holidays) ? holidays : [] })
      )
      .catch(() => ErrorMessage.SetErrorMessage('BD29'))
      .finally(() => this.setState({ loading: false }));
  }

  handleMutationResult(result) {
    if (result && (result.error || result.Error)) {
      ErrorMessage.SetErrorMessage(result.error || result.Error);
      return;
    }
    this.setState({ data: result || null, newFrom: '', newTo: '' });
  }

  handleHolidayMutationResult(result) {
    if (result && (result.error || result.Error)) {
      ErrorMessage.SetErrorMessage(result.error || result.Error);
      return;
    }
    this.setState({
      holidays: Array.isArray(result) ? result : [],
      newHolidayDate: '',
      newHolidayName: '',
    });
  }

  changeYear(delta) {
    this.setState({ year: this.state.year + delta }, () => this.load());
  }

  changeOffice(officeId) {
    this.setState({ officeId: Number(officeId) }, () => this.load());
  }

  addPair() {
    if (!this.state.newFrom || !this.state.newTo) {
      ErrorMessage.SetErrorMessage('Pick both a moved-from weekday and a moved-to weekend day.');
      return;
    }
    const item = {
      OfficeId: this.state.officeId,
      Year: this.state.year,
      MovedFromDate: this.state.newFrom,
      MovedToDate: this.state.newTo,
    };
    OfficeBridgeDayService.Save(item)
      .then((result) => this.handleMutationResult(result))
      .catch(() => ErrorMessage.SetErrorMessage('BD30'));
  }

  deletePair(id) {
    OfficeBridgeDayService.Delete(id)
      .then((result) => this.handleMutationResult(result))
      .catch(() => ErrorMessage.SetErrorMessage('BD31'));
  }

  toggleSkipped(skipped) {
    // Turning the switch on clears the year's pairs; warn first when there are any to lose.
    const pairs = this.state.data && Array.isArray(this.state.data.bridgeDays) ? this.state.data.bridgeDays : [];
    if (skipped && pairs.length > 0) {
      const count = pairs.length;
      ErrorMessage.SetApprove(
        `Turning on "No bridge days this year" will delete the ${count} configured bridge day${
          count > 1 ? 's' : ''
        } for this office and year.`,
        () => this.sendSkip(true),
        'DELETE AND CONTINUE?'
      );
      return;
    }
    this.sendSkip(skipped);
  }

  sendSkip(skipped) {
    const item = { OfficeId: this.state.officeId, Year: this.state.year, Skipped: skipped };
    OfficeBridgeDayService.SetSkipped(item)
      .then((result) => this.handleMutationResult(result))
      .catch(() => ErrorMessage.SetErrorMessage('BD32'));
  }

  addHoliday() {
    if (!this.state.newHolidayDate || !this.state.newHolidayName.trim()) {
      ErrorMessage.SetErrorMessage('Pick a date and give the holiday a name.');
      return;
    }
    const item = {
      OfficeId: this.state.officeId,
      HolidayDate: this.state.newHolidayDate,
      Name: this.state.newHolidayName.trim(),
    };
    PublicHolidayService.Save(item)
      .then((result) => this.handleHolidayMutationResult(result))
      .catch(() => ErrorMessage.SetErrorMessage('BD33'));
  }

  deleteHoliday(holiday) {
    // Imported rows are only tombstoned on the backend, so the future API reload won't bring them back.
    ErrorMessage.SetApprove(
      `Remove "${holiday.name}" (${this.formatDate(holiday.holidayDate)}) from this office's public holidays?`,
      () => {
        PublicHolidayService.Delete(holiday.publicHolidayId)
          .then((result) => this.handleHolidayMutationResult(result))
          .catch(() => ErrorMessage.SetErrorMessage('BD34'));
      }
    );
  }

  formatDate(iso) {
    if (!iso) return '';
    const date = new Date(iso.substring(0, 10));
    const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
    return `${iso.substring(0, 10)} (${weekday})`;
  }

  renderStatusBadge() {
    const status = this.state.data ? this.state.data.status : 'NotSet';
    const meta = STATUS[status] || STATUS.NotSet;
    return <span className={`badge ${meta.className}`}>{meta.label}</span>;
  }

  renderControls() {
    return (
      <div className='row p-1 align-items-center'>
        <div className='col-4'>
          <select
            className='form-select'
            value={this.state.officeId}
            onChange={(e) => this.changeOffice(e.target.value)}
          >
            {this.state.offices.map((o) => (
              <option
                key={o.officeId}
                value={o.officeId}
              >
                {o.name}
              </option>
            ))}
          </select>
        </div>
        <div className='col-1 text-center h6 mb-0'>
          <FaIcon
            className='icon oc-nav-icon'
            iconName='fa fa-chevron-left'
            tooltipText='Previous year'
            onClick={() => this.changeYear(-1)}
          />
        </div>
        <div className='col-2 text-center h6 mb-0 oc-year'>{this.state.year}</div>
        <div className='col-1 text-center h6 mb-0'>
          <FaIcon
            className='icon oc-nav-icon'
            iconName='fa fa-chevron-right'
            tooltipText='Next year'
            onClick={() => this.changeYear(1)}
          />
        </div>
        <div className='col-4 text-end'>{this.renderStatusBadge()}</div>
      </div>
    );
  }

  renderSkipToggle() {
    if (!this.canEdit) {
      return null;
    }
    const skipped = this.state.data ? this.state.data.skipped : false;
    return (
      <div className='p-1 ms-2'>
        <FormControlLabel
          control={
            <Switch
              checked={skipped}
              onChange={(e) => this.toggleSkipped(e.target.checked)}
            />
          }
          label='No bridge days this year'
        />
      </div>
    );
  }

  renderPairs() {
    const pairs = this.state.data && Array.isArray(this.state.data.bridgeDays) ? this.state.data.bridgeDays : [];
    if (pairs.length === 0) {
      return <div className='text-muted p-2'>No moved-day pairs configured for this office and year.</div>;
    }
    const sorted = [...pairs].sort((a, b) => a.movedFromDate.localeCompare(b.movedFromDate));
    return (
      <ul className='list-group'>
        {sorted.map((pair) => (
          <li
            key={pair.officeBridgeDayId}
            className='list-group-item d-flex justify-content-between align-items-center'
          >
            <span>
              {this.formatDate(pair.movedFromDate)}
              <i className='fa fa-arrow-right mx-2' />
              {this.formatDate(pair.movedToDate)}
            </span>
            {this.canEdit && (
              <FaIcon
                iconName='fa fa-trash text-danger'
                tooltipText='Remove this pair'
                onClick={() => this.deletePair(pair.officeBridgeDayId)}
              />
            )}
          </li>
        ))}
      </ul>
    );
  }

  renderAddForm() {
    // Shown to editors only (render() also hides it while the year is marked "no bridge days").
    if (!this.canEdit) {
      return null;
    }
    const min = `${this.state.year}-01-01`;
    const max = `${this.state.year}-12-31`;
    return (
      <div className='row p-1 mt-2 align-items-end'>
        <div className='col-4'>
          <label className='form-label small mb-0'>Moved-from (weekday)</label>
          <input
            type='date'
            className='form-control'
            min={min}
            max={max}
            value={this.state.newFrom}
            onChange={(e) => this.setState({ newFrom: e.target.value })}
          />
        </div>
        <div className='col-4'>
          <label className='form-label small mb-0'>Moved-to (weekend)</label>
          <input
            type='date'
            className='form-control'
            min={min}
            max={max}
            value={this.state.newTo}
            onChange={(e) => this.setState({ newTo: e.target.value })}
          />
        </div>
        <div className='col-2'>
          <button
            type='button'
            className='btn btn-primary'
            onClick={() => this.addPair()}
          >
            Add pair
          </button>
        </div>
      </div>
    );
  }

  renderHolidays() {
    const holidays = this.state.holidays;
    if (holidays.length === 0) {
      return <div className='text-muted p-2'>No public holidays configured for this office and year.</div>;
    }
    return (
      <ul className='list-group'>
        {holidays.map((holiday) => (
          <li
            key={holiday.publicHolidayId}
            className='list-group-item d-flex justify-content-between align-items-center'
          >
            <span>
              {this.formatDate(holiday.holidayDate)}
              <span className='mx-2'>{holiday.name}</span>
              {holiday.source === 'api' && <span className='badge bg-light text-secondary'>imported</span>}
            </span>
            {this.canEdit && (
              <FaIcon
                iconName='fa fa-trash text-danger'
                tooltipText='Remove this holiday'
                onClick={() => this.deleteHoliday(holiday)}
              />
            )}
          </li>
        ))}
      </ul>
    );
  }

  renderAddHolidayForm() {
    if (!this.canEdit) {
      return null;
    }
    const min = `${this.state.year}-01-01`;
    const max = `${this.state.year}-12-31`;
    return (
      <div className='row p-1 mt-2 align-items-end'>
        <div className='col-4'>
          <label className='form-label small mb-0'>Date</label>
          <input
            type='date'
            className='form-control'
            min={min}
            max={max}
            value={this.state.newHolidayDate}
            onChange={(e) => this.setState({ newHolidayDate: e.target.value })}
          />
        </div>
        <div className='col-4'>
          <label className='form-label small mb-0'>Name</label>
          <input
            type='text'
            className='form-control'
            maxLength={100}
            placeholder='e.g. National Day'
            value={this.state.newHolidayName}
            onChange={(e) => this.setState({ newHolidayName: e.target.value })}
          />
        </div>
        <div className='col-2'>
          <button
            type='button'
            className='btn btn-primary'
            onClick={() => this.addHoliday()}
          >
            Add holiday
          </button>
        </div>
      </div>
    );
  }

  render() {
    if (!this.canEdit) {
      return (
        <div className='contentwrapper bravo-ds oc-page p-3'>You are not authorized to view the Office Calendar.</div>
      );
    }
    if (this.state.loading) {
      return <Loading />;
    }
    // When the year is marked "no bridge days" there is nothing to list or add in the bridge-day section.
    const skipped = this.state.data ? this.state.data.skipped : false;
    return (
      // bravo-ds opts the page into the redesign scope; oc-page maps its text/lists onto the theme tokens
      // so the page stays readable in dark mode too.
      <div className='contentwrapper bravo-ds oc-page'>
        {this.renderControls()}
        <h5 className='p-1 mt-2'>Bridge days</h5>
        {this.renderSkipToggle()}
        {!skipped && <div className='p-1'>{this.renderPairs()}</div>}
        {!skipped && this.renderAddForm()}
        <h5 className='p-1 mt-4'>Public holidays</h5>
        <div className='p-1'>{this.renderHolidays()}</div>
        {this.renderAddHolidayForm()}
      </div>
    );
  }
}
